"use client";

/**
 * ApprovalCard — inline approval UX for GATE 3 tool gating.
 *
 * Renders four buttons when the agent's last turn created a pending decision
 * (via ChatResult.pendingDecisions[] from the backend). User picks a scope:
 *
 *   Allow Once — approve this one call
 *   Session    — auto-approve same (agent, tool) for this conversation
 *   Always     — auto-approve forever (inserts an approval_rules row)
 *   Deny       — reject this call
 *
 * On success, calls onResolved(scope) so the parent can replay the last
 * user message — the agent re-runs the turn and GATE 3 finds the
 * now-approved (or permanently-ruled) decision.
 *
 * Per 2026-04-22-inline-approval-ux-design §5.
 */

import { useState, useCallback } from "react";
import { Check, X, type LucideIcon } from "lucide-react";
import { api } from "@/lib/api";
import type { PendingDecision } from "./message-list";

type Scope = "once" | "session" | "always";
type Verb = "approved" | "denied";

interface ApprovalCardProps {
  decision: PendingDecision;
  onResolved?: (result: { verb: Verb; scope?: Scope }) => void;
}

interface ButtonSpec {
  key: Scope | "deny";
  label: string;
  verb: Verb;
  scope?: Scope;
  icon: LucideIcon;
  tone: "primary" | "secondary" | "danger";
}

const BUTTONS: ButtonSpec[] = [
  { key: "once",    label: "Allow Once", verb: "approved", scope: "once",    icon: Check, tone: "primary"   },
  { key: "session", label: "Session",    verb: "approved", scope: "session", icon: Check, tone: "secondary" },
  { key: "always",  label: "Always",     verb: "approved", scope: "always",  icon: Check, tone: "secondary" },
  { key: "deny",    label: "Deny",       verb: "denied",                     icon: X,     tone: "danger"    },
];

function toneClasses(tone: ButtonSpec["tone"]): string {
  switch (tone) {
    case "primary":
      return "bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90";
    case "secondary":
      return "bg-[var(--surface-high)] text-[var(--on-surface)] hover:bg-[var(--surface-container-high)] border border-[var(--outline-variant)]";
    case "danger":
      return "bg-[var(--error-container)] text-[var(--on-error-container)] hover:opacity-90";
  }
}

export function ApprovalCard({ decision, onResolved }: ApprovalCardProps) {
  const [state, setState] = useState<"idle" | "resolving" | "resolved" | "error">("idle");
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [resolvedLabel, setResolvedLabel] = useState<string>("");
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleClick = useCallback(
    async (button: ButtonSpec) => {
      if (state !== "idle") return;
      setState("resolving");
      setActiveKey(button.key);
      setErrorText(null);
      try {
        await api.decisions.resolve(decision.id, button.verb, undefined, button.scope);
        setResolvedLabel(
          button.verb === "denied" ? "Denied." : `Allowed (${button.scope}).`,
        );
        setState("resolved");
        onResolved?.({ verb: button.verb, scope: button.scope });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to resolve";
        setErrorText(msg);
        setState("idle");
        setActiveKey(null);
      }
    },
    [decision.id, state, onResolved],
  );

  if (state === "resolved") {
    return (
      <div className="mt-3 text-xs text-[var(--on-surface-variant)] italic">
        {resolvedLabel}
        {resolvedLabel.includes("Allowed") ? " Re-running…" : ""}
      </div>
    );
  }

  const toolLabel = decision.tool_name || "this action";

  return (
    <div className="mt-3 rounded-xl border border-[var(--outline-variant)] bg-[var(--surface-container)] p-4 space-y-3">
      <div className="text-xs text-[var(--on-surface-variant)]">
        Approval needed for <span className="font-semibold text-[var(--on-surface)]">{toolLabel}</span>
        {decision.description ? ` — ${decision.description}` : ""}
      </div>
      {errorText && (
        <div className="text-xs text-[var(--error)]">{errorText}</div>
      )}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {BUTTONS.map((b) => {
          const Icon = b.icon;
          const disabled = state === "resolving" && activeKey !== b.key;
          const spinning = state === "resolving" && activeKey === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => handleClick(b)}
              disabled={disabled || spinning}
              className={[
                "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition",
                toneClasses(b.tone),
                (disabled || spinning) ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              <Icon className="size-3.5" />
              {spinning ? "…" : b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
